import {
  Button,
  closeCurrentDialog,
  DialogsContext,
  openDialog,
  useAnimatedPopper,
} from 'platform/components';
import {Box, CSSDimension, HStack, Show, Text, VStack} from 'platform/foundation';

import {useEffect, useRef, useState, use} from 'react';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetAuthorizationProfileHasIntegrationQuery} from '@dms/api/security';
import {
  useGetMetadaServiceCaseQuery,
  usePostServiceOrderAddServicePackageMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError, useTenant} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {ServicePackages} from '../../../../../components/ServicePackages/ServicePackages';
import {ExternalPackagesDialog} from './ExternalPackagesDialog';

interface PackagesDropdownProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isDisabled?: boolean;
}

const HYUNDAI_SERVICE_INSPECTIONS_CZ_INTEGRATION_CODE = 'HYUNDAI_SERVICE_INSPECTIONS_CZ';
const GUTTER_PX = 4;

export function PackagesDropdown(props: PackagesDropdownProps) {
  const {isAnyDialogOpen} = use(DialogsContext);
  const {tenant} = useTenant();
  const {popperProps, Popper, togglePopper, referenceRef, isOpen} = useAnimatedPopper({
    placement: 'bottom-end',
    gutter: GUTTER_PX,
    isOutsideClickIgnored: isAnyDialogOpen,
    autoUpdateOptions: 'default',
  });
  const floatingElementRef = useRef<HTMLElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<CSSDimension | undefined>(undefined);

  const [postServiceOrderAddServicePackage] = usePostServiceOrderAddServicePackageMutation();
  const {data: serviceCaseData} = useGetMetadaServiceCaseQuery(
    {serviceCaseId: props.serviceCaseId},
    {
      skip: isNilOrEmpty(props.serviceCaseId),
    }
  );
  const {data: authorizationProfileHasIntegrationData} =
    useGetAuthorizationProfileHasIntegrationQuery(
      {
        authorizationProfileId: serviceCaseData?.authorizationProfileId ?? '',
        'x-tenant': tenant?.id ?? null,
      },
      {
        skip: isNilOrEmpty(serviceCaseData?.authorizationProfileId) || isNilOrEmpty(tenant?.id),
      }
    );

  const isHyundaiServiceInspectionsCzIntegrationEnabled = Boolean(
    authorizationProfileHasIntegrationData?.integration?.some(
      (integration) => integration?.code === HYUNDAI_SERVICE_INSPECTIONS_CZ_INTEGRATION_CODE
    )
  );

  const handleAddServicePackage = async (servicePackageId: string) => {
    await postServiceOrderAddServicePackage({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
      servicePackageId,
    })
      .unwrap()
      .catch(handleApiError);
  };

  useEffect(() => {
    if (!isOpen || !floatingElementRef.current) {
      setMaxHeight(undefined);
      return;
    }

    const calculateMaxHeight = () => {
      if (!floatingElementRef.current) {
        return;
      }

      const rect = floatingElementRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - rect.top - GUTTER_PX;

      setMaxHeight(`calc(${Math.max(0, availableHeight)}px)` as CSSDimension);
    };

    requestAnimationFrame(() => {
      calculateMaxHeight();
    });

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(calculateMaxHeight);
    });
    resizeObserver.observe(window.document.body);

    const handleResize = () => {
      requestAnimationFrame(calculateMaxHeight);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', calculateMaxHeight, true);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', calculateMaxHeight, true);
    };
  }, [isOpen]);

  const openExternalPackagesDialog = () => {
    openDialog(
      <ExternalPackagesDialog
        serviceCaseId={props.serviceCaseId}
        serviceOrderId={props.serviceOrderId}
        onClose={closeCurrentDialog}
        data-testid={suffixTestId('externalPackages', props)}
      />,
      {
        title: i18n.t('entity.integration.labels.externalPackages'),
        size: 'xLarge',
        withAdditionalFooter: true,
        disableBodyPadding: true,
      }
    );
  };

  const handleFloatingRef = (node: HTMLElement | null) => {
    floatingElementRef.current = node;
    popperProps.floatingRef?.(node);
  };

  return (
    <>
      <div ref={referenceRef}>
        <Button
          title={i18n.t('entity.workshopServicePackages.labels.packages')}
          variant="outlined"
          size="small"
          isDisabled={props.isDisabled}
          onClick={(event) => {
            event.stopPropagation();
            togglePopper();
          }}
          data-testid={props['data-testid']}
        />
      </div>

      <Popper {...popperProps} floatingRef={handleFloatingRef}>
        <div onClick={(event) => event.stopPropagation()}>
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
            padding={3}
            width={116}
            maxHeight={maxHeight}
            overflow="auto"
          >
            <VStack spacing={4}>
              <HStack justify="space-between" align="center">
                <Text>{i18n.t('entity.workshopServicePackages.labels.packages')}</Text>
                <Show when={isHyundaiServiceInspectionsCzIntegrationEnabled}>
                  <Button
                    title={i18n.t('entity.integration.labels.externalPackages')}
                    variant="secondary"
                    leftIcon="content/save_alt"
                    onClick={openExternalPackagesDialog}
                    data-testid={suffixTestId('externalPackages', props)}
                  />
                </Show>
              </HStack>
              <ServicePackages
                isWithoutTitle
                serviceCaseId={props.serviceCaseId}
                onServicePackageAdd={handleAddServicePackage}
                data-testid={props['data-testid']}
              />
            </VStack>
          </Box>
        </div>
      </Popper>
    </>
  );
}
